import {
  BarChart3,
  BookOpen,
  Building2,
  FileText,
  Landmark,
  Percent,
  type LucideIcon,
} from "lucide-react";

export type ReferenceAccent = "primary" | "lilac" | "sage" | "ochre" | "amber";

export type ReferenceTermAvailability =
  | "platform"
  | "conditional"
  | "educational";

export type ReferenceVisualKind =
  | "bars"
  | "scale"
  | "flow"
  | "timeline"
  | "stack"
  | "comparison"
  | "reading";

export type ReferenceVisualTone = "accent" | "positive" | "warning" | "muted";

export type ReferenceVisualItem = {
  label: string;
  value: string;
  ratio?: number;
  tone?: ReferenceVisualTone;
};

export type ReferenceVisual = {
  kind: ReferenceVisualKind;
  title: string;
  description: string;
  items: ReferenceVisualItem[];
  marker?: number;
  markerLabel?: string;
  leftLabel?: string;
  rightLabel?: string;
  note?: string;
};

export type ReferenceTerm = {
  id: string;
  title: string;
  summary: string;
  availability?: ReferenceTermAvailability;
  visual?: ReferenceVisual;
  formula?: string;
  bullets?: string[];
  warning?: string;
  aliases?: string[];
  cta?: { label: string; href: string };
};

export type ReferenceSection = {
  id: string;
  title: string;
  description: string;
  cardDescription: string;
  icon: LucideIcon;
  accent: ReferenceAccent;
  tags: string[];
  terms: ReferenceTerm[];
};

export type ReferenceCategory = Pick<
  ReferenceSection,
  "id" | "title" | "cardDescription" | "icon" | "accent" | "tags"
>;

const referenceSectionDefinitions: ReferenceSection[] = [
  {
    id: "indicadores",
    title: "Indicadores de FIIs",
    description:
      "Métricas calculadas ou exibidas no Catálogo, Rankings, Comparador e páginas de fundos. A disponibilidade depende da cobertura dos dados.",
    cardDescription: "Métricas que você encontra no produto.",
    icon: BarChart3,
    accent: "primary",
    tags: ["DY", "P/VP", "Liquidez 30d", "+14"],
    terms: [
      {
        id: "dividend-yield",
        title: "Dividend Yield (DY)",
        summary:
          "Mede quanto os rendimentos distribuídos representam em relação ao preço da cota na janela analisada. É uma medida de renda passada, não uma promessa de retorno.",
        availability: "platform",
        formula:
          "DY 12m ≈ soma dos rendimentos considerados na janela de 12 meses ÷ cotação usada na referência × 100",
        bullets: [
          "Na plataforma, você pode encontrar o DY de 12 meses e o DY do mês, sempre associados a uma data de referência.",
          "Compare fundos com estratégia, qualidade de ativos, risco e metodologia semelhantes.",
          "Separe renda recorrente de amortizações, vendas de ativos ou outros eventos extraordinários.",
          "Um DY alto pode resultar de queda da cotação ou de aumento de risco, e não apenas de uma renda melhor.",
        ],
        warning:
          "DY histórico não é promessa de rendimento futuro. Confira a janela, a data e os eventos incluídos antes de comparar fundos.",
        aliases: ["dy", "dividend yield", "yield de dividendos", "rendimento"],
        cta: { label: "Ver este indicador nos FIIs", href: "/catalogo" },
      },
      {
        id: "p-vp",
        title: "P/VP — Preço sobre Valor Patrimonial",
        summary:
          "Compara o preço de mercado da cota com o valor patrimonial por cota informado pelo fundo. Ajuda a descrever prêmio ou desconto relativo ao patrimônio contábil.",
        availability: "platform",
        formula: "P/VP = preço da cota ÷ valor patrimonial por cota",
        bullets: [
          "1,00 indica preço próximo do valor patrimonial informado na mesma referência.",
          "Abaixo de 1 pode indicar desconto relativo; acima de 1 indica prêmio relativo.",
          "O VP depende de avaliações, provisões, dívidas e demais componentes do patrimônio.",
          "Leia o indicador junto da qualidade dos ativos, gestão, liquidez, renda e expectativas.",
        ],
        warning:
          "Um desconto pode refletir problemas reais no portfólio, risco de crédito, baixa liquidez ou defasagem da avaliação patrimonial. P/VP abaixo de 1 não significa automaticamente que a cota está barata.",
        aliases: [
          "pvp",
          "p/vp",
          "preço sobre valor patrimonial",
          "desconto patrimonial",
        ],
        cta: { label: "Comparar P/VP dos fundos", href: "/comparador" },
      },
      {
        id: "vp-cota",
        title: "Valor patrimonial por cota (VP/cota)",
        summary:
          "É uma estimativa do patrimônio líquido atribuído a cada cota na data de referência. No produto, aparece junto da cotação para ajudar a contextualizar o P/VP.",
        availability: "platform",
        formula: "VP/cota = patrimônio líquido ÷ número de cotas",
        bullets: [
          "O patrimônio pode incluir imóveis, CRIs, cotas de fundos, caixa e outros ativos, menos obrigações.",
          "Avaliações contábeis e regulatórias, provisões e movimentações do fundo influenciam o valor informado.",
          "Compare VP/cota e cotação com datas compatíveis; uma série pode ter defasagens diferentes.",
          "A evolução do VP/cota em 12 meses é uma métrica separada e não equivale ao retorno da cota.",
        ],
        warning:
          "O VP/cota não é um preço de venda garantido dos imóveis e não substitui a leitura da composição e da qualidade do patrimônio.",
        aliases: [
          "vp por cota",
          "valor patrimonial",
          "valor patrimonial por cota",
          "nav",
        ],
      },
      {
        id: "liquidez-media",
        title: "Liquidez diária (30d)",
        summary:
          "Indica o valor financeiro médio negociado por dia nos últimos 30 pregões. É uma referência para a facilidade de executar uma compra ou venda.",
        availability: "platform",
        formula:
          "Liquidez diária (30d) = volume financeiro negociado ÷ número de pregões considerados",
        bullets: [
          "O catálogo calcula o volume usando preço de fechamento × volume negociado e exibe a média dos últimos 30 pregões.",
          "Maior liquidez tende a facilitar a entrada e a saída, mas não garante preço melhor.",
          "Negociação mais intensa pode reduzir o impacto de ordens maiores, embora o spread e o livro de ofertas também importem.",
          "Baixa liquidez não define a qualidade do fundo, mas aumenta o risco de execução e de diferença entre preço esperado e realizado.",
        ],
        warning:
          "A liquidez é uma fotografia recente do mercado. Ela pode mudar rapidamente e não deve ser confundida com o tamanho do patrimônio do fundo.",
        aliases: [
          "liquidez",
          "volume médio",
          "volume diário",
          "bid ask",
          "spread",
        ],
        cta: { label: "Filtrar fundos por liquidez", href: "/catalogo" },
      },
      {
        id: "vacancia-fisica",
        title: "Vacância física",
        summary:
          "É o percentual da área locável que está desocupada. No produto, pode aparecer como vacância média agregada dos imóveis reportados.",
        availability: "conditional",
        formula:
          "Vacância física = área vaga ÷ ABL total × 100, quando essa metodologia é informada",
        bullets: [
          "Uma vacância menor costuma significar mais área ocupada, mas não elimina risco de crédito, concentração ou renegociação.",
          "Observe localização, qualidade do imóvel, prazo dos contratos e velocidade de reposição da área.",
          "Vacância física mede espaço; vacância financeira considera o efeito econômico sobre a receita. As duas podem contar histórias diferentes.",
          "A cobertura depende dos imóveis e das informações da base trimestral mais recente.",
        ],
        warning:
          "Ausência de valor não significa vacância zero. Verifique a competência, a fonte e a cobertura antes de comparar fundos.",
        aliases: ["vacância", "vacancia", "ocupação física", "ocupacao"],
      },
      {
        id: "inadimplencia",
        title: "Inadimplência média",
        summary:
          "Indica o percentual médio de receitas imobiliárias em atraso nos dados de imóveis reportados.",
        availability: "conditional",
        formula:
          "Inadimplência média ≈ média ponderada pelos pesos de receita dos imóveis informados",
        bullets: [
          "Quando há pesos de receita, a plataforma prioriza uma média ponderada; sem esses pesos, usa a média dos valores disponíveis.",
          "Uma média baixa não elimina risco de concentração em um devedor ou de recuperação lenta.",
          "Leia junto de vacância, contratos, garantias, concentração e histórico de recebimentos.",
          "A competência e a quantidade de imóveis reportados são tão importantes quanto o percentual.",
        ],
        warning:
          "O indicador não representa necessariamente a inadimplência de todo o patrimônio do fundo: ele depende da cobertura e da metodologia dos imóveis informados.",
        aliases: ["inadimplência", "inadimplencia", "atraso de aluguel"],
      },
      {
        id: "volatilidade-21d",
        title: "Volatilidade anualizada (21d)",
        summary:
          "Estima quanto a cotação oscilou recentemente, anualizando a dispersão dos retornos dos últimos 21 pregões disponíveis.",
        availability: "platform",
        formula:
          "Volatilidade ≈ desvio-padrão dos retornos diários recentes × √252",
        bullets: [
          "O valor é anualizado para facilitar a comparação entre fundos, mas nasce de uma janela curta.",
          "Volatilidade mede oscilação de preço, não qualidade do patrimônio, renda ou probabilidade de perda.",
          "Compare fundos em períodos semelhantes e considere liquidez, eventos corporativos e contexto de mercado.",
          "Com histórico insuficiente, o indicador pode não estar disponível.",
        ],
        warning:
          "É uma medida retrospectiva de mercado, não uma previsão. Uma volatilidade baixa também pode refletir pouca negociação.",
        aliases: ["volatilidade", "volatilidade 21d", "risco de preço"],
      },
      {
        id: "dy-mes",
        title: "DY do mês",
        summary:
          "Mostra a relação entre os rendimentos distribuídos em um mês e a cotação usada para a referência.",
        availability: "platform",
        formula:
          "DY do mês ≈ rendimento distribuído no mês ÷ cotação usada na referência × 100",
        bullets: [
          "Serve para observar a distribuição de um mês específico, sem substituir o histórico de 12 meses.",
          "Um mês isolado pode conter efeitos sazonais, extraordinários ou diferenças de data de pagamento.",
          "Compare a série mensal para avaliar regularidade, crescimento e mudanças de patamar.",
          "A cotação usada altera o percentual mesmo quando o valor distribuído não muda.",
        ],
        warning:
          "Não anualize automaticamente o DY de um único mês: isso pode transformar uma distribuição atípica em uma expectativa enganosa.",
        aliases: ["dy mensal", "dividend yield mensal", "rendimento do mês"],
      },
      {
        id: "crescimento-dy",
        title: "Crescimento do DY (12m)",
        summary:
          "Compara o total de rendimentos distribuídos nos 12 meses atuais com os 12 meses anteriores para mostrar mudança no nível de renda.",
        availability: "platform",
        formula:
          "Crescimento 12m = (rendimentos atuais − rendimentos anteriores) ÷ |rendimentos anteriores| × 100",
        bullets: [
          "Crescimento positivo indica que a soma distribuída na janela atual aumentou em relação à janela anterior.",
          "Crescimento negativo pode refletir menor distribuição ou mudança na composição da renda.",
          "Esse campo acompanha os rendimentos distribuídos; a cotação entra no cálculo do DY, mas não explica sozinha este crescimento.",
          "Leia junto da evolução dos dividendos em reais, da cotação e de eventos extraordinários.",
        ],
        warning:
          "A variação dos rendimentos em 12 meses não é igual ao retorno do cotista e não deve ser interpretada isoladamente como crescimento sustentável.",
        aliases: ["crescimento dy", "dy growth", "evolução do dy"],
      },
      {
        id: "desconto-pvp-historico",
        title: "Desconto ou prêmio do P/VP histórico",
        summary:
          "Compara o P/VP atual com a média histórica recente do próprio fundo, em vez de compará-lo apenas com 1,00.",
        availability: "platform",
        formula:
          "Desconto histórico = (P/VP médio de até 12 meses − P/VP atual) ÷ média histórica × 100",
        bullets: [
          "Resultado positivo indica P/VP atual abaixo da média histórica; negativo indica acima dela.",
          "A média usa os pontos mensais disponíveis, que podem ser menos de 12 quando há lacunas.",
          "Uma mudança estrutural no fundo pode tornar o passado menos comparável ao presente.",
          "Use como contexto de preço, não como prova de oportunidade ou de risco baixo.",
        ],
        warning:
          "A média histórica não é um valor justo. Ela pode carregar períodos com outra carteira, outro nível de juros ou outro risco.",
        aliases: [
          "desconto p/vp",
          "pvp histórico",
          "prêmio histórico",
          "desconto histórico",
        ],
        cta: { label: "Comparar P/VP histórico", href: "/comparador" },
      },
      {
        id: "distancia-maxima-12m",
        title: "Distância da máxima (12m)",
        summary:
          "Mostra quanto a cotação atual está abaixo da maior cotação observada nos 12 meses anteriores.",
        availability: "platform",
        formula:
          "Distância da máxima = (máxima de 12 meses − preço atual) ÷ máxima de 12 meses × 100",
        bullets: [
          "Quanto maior o percentual, mais distante a cotação está do topo recente.",
          "Estar distante da máxima não significa estar barato; o topo pode ter sido temporário ou irracional.",
          "Combine com fundamentos, resultados, liquidez e acontecimentos recentes do fundo.",
        ],
        warning:
          "A máxima passada é apenas uma referência de preço. Ela não representa valor justo nem uma meta de recuperação.",
        aliases: [
          "distância da máxima",
          "distancia maxima",
          "drawdown do topo",
          "máxima 12m",
        ],
      },
      {
        id: "crescimento-vp-cota",
        title: "Crescimento do VP/cota (12m)",
        summary:
          "Mede a variação percentual do valor patrimonial por cota em relação ao ponto de aproximadamente 12 meses antes.",
        availability: "platform",
        formula:
          "Crescimento do VP/cota = (VP/cota atual ÷ VP/cota de 12m atrás − 1) × 100",
        bullets: [
          "Pode refletir valorização ou desvalorização de ativos, resultados retidos, emissões, amortizações e mudanças no número de cotas.",
          "Não é o mesmo que retorno do cotista: não incorpora automaticamente dividendos, preço de compra ou reinvestimento.",
          "Confira se os pontos comparados pertencem a competências compatíveis.",
        ],
        warning:
          "Crescimento patrimonial não garante aumento de renda nem valorização da cotação.",
        aliases: ["crescimento vp", "vp por cota 12m", "evolução patrimonial"],
      },
      {
        id: "caixa-liquidez-fundo",
        title: "Caixa, recebíveis e liquidez líquida",
        summary:
          "Organiza os recursos de curto prazo do fundo e o que sobra depois de considerar valores a receber e obrigações reportadas.",
        availability: "conditional",
        formula:
          "Liquidez líquida = disponibilidades + recebíveis − obrigações",
        bullets: [
          "A tela de detalhes pode mostrar caixa, recebíveis, obrigações, liquidez líquida e caixa sobre o patrimônio investido.",
          "Caixa equivalente inclui disponibilidades e determinados títulos de alta liquidez conforme a classificação da plataforma.",
          "Recursos de caixa podem financiar despesas, amortizações e aquisições, mas também podem indicar capital ainda não alocado.",
          "Compare a liquidez com o tamanho do fundo e com suas obrigações, não apenas em valor absoluto.",
        ],
        warning:
          "É uma fotografia contábil da competência disponível. A composição exata depende dos campos reportados pelo fundo e pela fonte CVM.",
        aliases: [
          "caixa",
          "liquidez líquida",
          "recebíveis",
          "obrigações",
          "caixa sobre patrimônio",
        ],
      },
      {
        id: "pressao-caixa",
        title: "Pressão e tendência do caixa",
        summary:
          "Mostra em quantas competências mensais recentes o caixa líquido ficou negativo e se a situação melhorou ou piorou contra a competência anterior.",
        availability: "conditional",
        formula:
          "Meses sob pressão (12m) = competências com caixa líquido negativo nos últimos 12 meses",
        bullets: [
          "A tendência compara o caixa líquido atual com o da competência anterior e classifica o movimento.",
          "Mais meses sob pressão podem sinalizar dependência de novas receitas, vendas, captações ou caixa acumulado.",
          "O indicador deve ser lido junto de vacância, inadimplência, amortizações, obras e obrigações futuras.",
          "Zero meses sob pressão não significa que o fundo não tenha risco financeiro.",
        ],
        warning:
          "A métrica usa caixa líquido mensal reportado e não é uma previsão de distribuição de dividendos.",
        aliases: [
          "pressão de caixa",
          "tempo sob pressão",
          "tendência de caixa",
          "resultado de caixa",
        ],
      },
      {
        id: "concentracao-tijolo-papel",
        title: "Concentração em tijolo e papel",
        summary:
          "Indica quanto do perfil patrimonial analisado está concentrado em imóveis e direitos reais (tijolo) ou em ativos financeiros e recebíveis (papel).",
        availability: "conditional",
        formula:
          "Concentração do perfil = maior posição do perfil ÷ total do próprio perfil × 100",
        bullets: [
          "A concentração em tijolo ajuda a contextualizar dependência de imóveis, locatários e contratos.",
          "A concentração em papel ajuda a contextualizar dependência de devedores, operações e estruturas de crédito.",
          "Diversificação por quantidade de ativos não garante diversificação por risco, região ou devedor.",
          "A metodologia e a cobertura podem variar conforme os ativos identificados no informe.",
        ],
        warning:
          "Esses percentuais não são uma classificação universal do fundo e podem não somar 100% quando há outras classes de ativos ou dados não mapeados.",
        aliases: [
          "concentração tijolo",
          "concentração papel",
          "mix tijolo papel",
          "perfil patrimonial",
        ],
      },
      {
        id: "sensibilidade-selic",
        title: "Sensibilidade à Selic",
        summary:
          "Score da plataforma para contextualizar como a composição financeira, os indexadores e a exposição a ativos de crédito podem reagir a cenários de juros.",
        availability: "conditional",
        bullets: [
          "O score combina características da carteira de ativos financeiros, exposição à inflação, fundos investidos e indexador predominante.",
          "Ele não prevê a próxima decisão do Banco Central nem mede diretamente a variação futura do preço.",
          "Fundos diferentes podem receber scores semelhantes por motivos econômicos diferentes.",
          "Use o score como ponto de partida e leia a composição, os indexadores e os prazos.",
        ],
        warning:
          "É um índice composto próprio da plataforma, não uma medida oficial ou uma recomendação sobre o efeito de uma alta ou queda da Selic.",
        aliases: [
          "selic",
          "sensibilidade a juros",
          "exposição à selic",
          "juros",
        ],
      },
      {
        id: "indice-alavancagem",
        title: "Índice de alavancagem",
        summary:
          "Score da plataforma para sinalizar pressão financeira a partir de caixa, recebíveis, obrigações, liquidez líquida e patrimônio líquido.",
        availability: "conditional",
        bullets: [
          "O score procura contextualizar o peso das obrigações e a folga financeira observada nos dados disponíveis.",
          "A leitura deve começar pelos valores absolutos de passivos, caixa e liquidez líquida, não apenas pela faixa do score.",
          "Uma alavancagem maior pode ampliar ganhos ou perdas e aumentar a sensibilidade a receitas e juros.",
          "O score não substitui a leitura de contratos de dívida, garantias, covenants e cronograma de vencimentos.",
        ],
        warning:
          "Índice de alavancagem não é LTV e não representa uma razão única entre dívida e valor de garantias.",
        aliases: [
          "alavancagem",
          "índice de dívida",
          "pressão financeira",
          "leverage",
        ],
      },
    ],
  },
  {
    id: "conceitos",
    title: "Conceitos de FIIs",
    description:
      "Entenda como os fundos funcionam, como são classificados e quais eventos afetam o cotista.",
    cardDescription: "Tipos de fundo, eventos e estrutura.",
    icon: Building2,
    accent: "lilac",
    tags: ["Tijolo", "Papel", "FoF", "+12"],
    terms: [
      {
        id: "fii",
        title: "FII — Fundo de Investimento Imobiliário",
        summary:
          "Estrutura de investimento coletivo voltada a imóveis, direitos sobre imóveis e ativos ligados ao mercado imobiliário.",
        bullets: [
          "A política pode permitir imóveis, CRIs, LCIs, cotas de outros fundos e outros ativos.",
          "FIIs são fundos fechados: normalmente o cotista sai vendendo suas cotas no mercado secundário.",
          "A cota pode subir ou cair e o rendimento pode variar.",
        ],
        aliases: ["fundo imobiliário", "fundos imobiliários", "fii"],
      },
      {
        id: "cota",
        title: "Cota",
        summary:
          "É a fração ideal do patrimônio da classe ou do fundo que o investidor adquire.",
        bullets: [
          "O cotista participa na proporção correspondente ao número de cotas.",
          "Comprar uma cota não significa ser proprietário direto de um imóvel específico.",
          "No mercado secundário, a negociação ocorre entre investidores.",
        ],
        aliases: ["cotas", "cotista", "fração ideal"],
      },
      {
        id: "fii-tijolo",
        title: "FII de tijolo",
        summary:
          "Fundo que investe predominantemente em imóveis ou direitos ligados a imóveis físicos.",
        bullets: [
          "A renda pode vir de aluguéis, exploração ou venda de ativos.",
          "Setores comuns incluem logística, escritórios, shoppings, hospitais e renda urbana.",
          "Localização, vacância, contratos e concentração são pontos importantes de análise.",
        ],
        aliases: ["tijolo", "fundo de imóveis", "imobiliário físico"],
        cta: { label: "Explorar fundos de tijolo", href: "/catalogo" },
      },
      {
        id: "fii-papel",
        title: "FII de papel",
        summary:
          "Nome de mercado para fundos com forte exposição a títulos e valores mobiliários imobiliários, especialmente CRIs.",
        bullets: [
          "A renda é influenciada por indexadores, spreads, amortizações e eventos de crédito.",
          "A análise precisa considerar devedores, garantias, concentração e estrutura da operação.",
          "Papel não significa ausência de risco imobiliário ou de mercado.",
        ],
        aliases: ["papel", "recebíveis", "fii de recebíveis"],
      },
      {
        id: "fof",
        title: "FoF — Fund of Funds",
        summary: "Fundo que investe em cotas de outros fundos.",
        bullets: [
          "Pode oferecer diversificação e gestão profissional de alocação.",
          "Observe custos em camadas e o prêmio ou desconto dos fundos investidos.",
          "Giro de carteira e estratégia do gestor podem mudar bastante o perfil do FoF.",
        ],
        aliases: ["fund of funds", "fundo de fundos", "fof"],
      },
      {
        id: "emissao-cotas",
        title: "Emissão de cotas",
        summary:
          "Criação e oferta de novas cotas para captar recursos conforme a estratégia do fundo.",
        bullets: [
          "Os recursos podem financiar aquisições, projetos, redução de dívida ou expansão.",
          "O preço da emissão e o uso do capital influenciam o efeito econômico para os cotistas.",
          "Direito de preferência, sobras e montante adicional seguem as regras de cada oferta.",
        ],
        aliases: ["emissão", "oferta", "subscrição", "direito de preferência"],
      },
      {
        id: "amortizacao-cotas",
        title: "Amortização de cotas",
        summary:
          "Devolução de parte do capital ao cotista, reduzindo economicamente o investimento correspondente.",
        bullets: [
          "Pode ocorrer após venda de ativos, pagamento de principal ou liquidação parcial.",
          "Não é a mesma coisa que rendimento.",
          "O comunicado do fundo explica a origem e as regras do evento.",
        ],
        aliases: ["amortização", "amortizacao", "devolução de capital"],
      },
      {
        id: "relatorio-gerencial",
        title: "Relatório gerencial",
        summary:
          "Documento periódico com comentários da gestão sobre carteira, resultados, eventos e estratégia.",
        bullets: [
          "É uma fonte importante para entender o fundo e acompanhar mudanças.",
          "Representa a visão da gestão e deve ser complementado por documentos regulatórios.",
          "Compare o comentário com os números e com o histórico do portfólio.",
        ],
        aliases: ["relatório", "relatorio gerencial", "informe gerencial"],
      },
    ],
  },
  {
    id: "imoveis-contratos",
    title: "Imóveis e contratos",
    description:
      "Termos usados para avaliar imóveis, ocupação, locatários e contratos.",
    cardDescription: "Imóveis, ocupação e contratos.",
    icon: Landmark,
    accent: "sage",
    tags: ["ABL", "WAULT", "Vacância", "+10"],
    terms: [
      {
        id: "abl",
        title: "ABL — Área Bruta Locável",
        summary:
          "Área de um empreendimento considerada disponível para locação segundo a metodologia adotada.",
        availability: "conditional",
        bullets: [
          "É muito usada em shopping centers, galpões e escritórios.",
          "Compare sempre a definição e a unidade apresentadas pelo fundo.",
          "ABL ajuda a colocar aluguel, ocupação e vendas em uma base comparável.",
        ],
        warning:
          "A plataforma pode receber a área em informes de imóveis, mas não há um campo de ABL consolidado para todos os fundos.",
        aliases: [
          "área bruta locável",
          "area bruta locavel",
          "área locável",
          "area locavel",
        ],
      },
      {
        id: "contrato-tipico",
        title: "Contrato típico",
        summary:
          "Contrato de locação sujeito ao regime comum aplicável às locações.",
        availability: "conditional",
        bullets: [
          "Pode permitir revisões e renegociações dentro das regras legais e contratuais.",
          "Prazo, reajuste, garantias e capacidade do locatário continuam relevantes.",
          "A classificação não informa sozinha a segurança da renda.",
        ],
        aliases: ["locação típica", "locacao tipica", "contrato de aluguel"],
      },
      {
        id: "contrato-atipico",
        title: "Contrato atípico",
        summary:
          "Contrato estruturado com obrigações econômicas específicas, frequentemente de prazo mais longo.",
        availability: "conditional",
        bullets: [
          "Pode prever multas e condições específicas de saída.",
          "Built-to-suit e sale and leaseback são estruturas frequentemente associadas a contratos atípicos.",
          "Contrato atípico não significa ausência de risco de crédito ou execução.",
        ],
        aliases: [
          "locação atípica",
          "locacao atipica",
          "contrato de longo prazo",
        ],
      },
      {
        id: "built-to-suit",
        title: "Built-to-suit (BTS)",
        summary:
          "Operação em que um imóvel é desenvolvido ou adaptado para atender às necessidades de um ocupante específico.",
        bullets: [
          "Pode gerar contrato de longo prazo e ativo já adequado ao uso.",
          "Avalie concentração, crédito do locatário e valor de reutilização do imóvel.",
          "Obra, licenciamento e execução também fazem parte da análise.",
        ],
        aliases: ["bts", "built to suit", "construído sob medida"],
      },
      {
        id: "sale-leaseback",
        title: "Sale and leaseback (SLB)",
        summary:
          "A empresa vende um imóvel e permanece como locatária do ativo.",
        bullets: [
          "Para o fundo, a operação combina aquisição de imóvel e relação de locação.",
          "Preço, aluguel, prazo, crédito e uso alternativo precisam ser avaliados.",
          "A permanência do antigo proprietário não elimina risco de locatário.",
        ],
        aliases: ["slb", "sale leaseback", "venda e locação"],
      },
      {
        id: "noi",
        title: "NOI — Net Operating Income",
        summary:
          "Resultado operacional líquido do imóvel antes de itens financeiros e algumas despesas não operacionais.",
        availability: "educational",
        formula: "NOI ≈ receitas operacionais − despesas operacionais",
        bullets: [
          "A metodologia exata pode variar entre relatórios.",
          "Ajuda a observar a capacidade operacional de geração de renda do ativo.",
          "Leia junto de vacância, incentivos e custos de manutenção.",
        ],
        warning:
          "A plataforma não calcula um NOI padronizado por ticker. A metodologia pode variar entre relatórios e imóveis.",
        aliases: [
          "net operating income",
          "resultado operacional líquido",
          "resultado operacional",
        ],
      },
      {
        id: "revisional",
        title: "Revisional de aluguel",
        summary:
          "Mecanismo para ajustar o aluguel ao valor de mercado conforme contrato e legislação.",
        bullets: [
          "Não é a mesma coisa que o reajuste anual por inflação.",
          "Pode elevar ou reduzir a receita conforme o aluguel vigente esteja acima ou abaixo do mercado.",
          "Observe a concentração de revisões no portfólio.",
        ],
        aliases: ["revisional", "revisão de aluguel", "revisao de aluguel"],
      },
      {
        id: "concentracao-inquilino",
        title: "Concentração por inquilino",
        summary:
          "Percentual da receita ou da área ligado a um mesmo locatário.",
        availability: "educational",
        bullets: [
          "Um fundo pode ter baixa vacância e ainda depender muito de um único inquilino.",
          "Avalie setor, saúde financeira, prazo e possibilidade de reposição.",
          "A diversificação por quantidade de locatários não garante diversificação de risco.",
        ],
        aliases: [
          "concentração de locatário",
          "concentracao por inquilino",
          "monoinquilino",
        ],
      },
      {
        id: "cap-rate",
        title: "Cap rate",
        summary:
          "Estima a taxa de retorno operacional anual de um imóvel em relação ao valor atribuído a esse imóvel.",
        formula: "Cap rate = NOI anualizado ÷ valor do imóvel × 100",
        bullets: [
          "É uma métrica de um imóvel ou conjunto de imóveis, não o mesmo que o DY distribuído pelo FII.",
          "Um cap rate maior pode refletir renda operacional maior, preço menor ou risco maior.",
          "Compare imóveis semelhantes em localização, padrão, ocupação, contrato e qualidade do locatário.",
          "Juros de mercado, crescimento esperado do aluguel e despesas influenciam a taxa exigida.",
        ],
        warning:
          "A plataforma não calcula cap rate padronizado por ticker. Para isso, seriam necessários NOI e valor de cada imóvel com metodologia consistente.",
        aliases: ["cap rate", "taxa de capitalização", "capitalização"],
      },
      {
        id: "wault",
        title: "WAULT",
        summary:
          "Prazo médio ponderado restante dos contratos de locação, usado para estimar por quanto tempo a receita contratada permanece vigente.",
        formula: "WAULT = Σ(peso do contrato × prazo restante do contrato)",
        bullets: [
          "A ponderação pode usar aluguel, receita, área ou outro critério definido pelo fundo.",
          "Prazo maior pode dar previsibilidade à receita, mas não elimina risco de crédito, vacância ou concentração.",
          "Contratos com vencimentos longos podem ter reajustes, revisões, multas e condições diferentes.",
          "Consulte também a distribuição dos vencimentos e a qualidade dos locatários.",
        ],
        warning:
          "A plataforma não calcula WAULT padronizado: os dados de contratos disponíveis são incompletos ou textuais para muitos fundos.",
        aliases: [
          "walt",
          "weighted average unexpired lease term",
          "prazo médio dos contratos",
          "prazo dos contratos",
        ],
      },
    ],
  },
  {
    id: "credito-papel",
    title: "Crédito e FIIs de papel",
    description:
      "Conceitos para entender CRIs, garantias, risco de crédito e estrutura das operações.",
    cardDescription: "Crédito, CRIs e garantias.",
    icon: FileText,
    accent: "ochre",
    tags: ["CRI", "LTV", "Rating", "+14"],
    terms: [
      {
        id: "cri",
        title: "CRI — Certificado de Recebíveis Imobiliários",
        summary:
          "Título emitido por uma companhia securitizadora e lastreado em créditos imobiliários.",
        bullets: [
          "O pagamento depende da estrutura da operação, dos créditos e das garantias.",
          "Securitização transforma direitos creditórios em títulos negociáveis.",
          "CRI não possui cobertura do FGC.",
        ],
        aliases: [
          "certificado de recebíveis imobiliários",
          "recebível imobiliário",
          "cri",
        ],
        cta: { label: "Ver conceitos de crédito", href: "/catalogo" },
      },
      {
        id: "securitizacao",
        title: "Securitização",
        summary:
          "Processo de transformar direitos creditórios em títulos negociáveis.",
        bullets: [
          "A securitizadora estrutura créditos, garantias e fluxos de pagamento.",
          "O investidor deve entender quem paga, qual é o lastro e como as perdas são absorvidas.",
          "A estrutura jurídica e os documentos da emissão fazem diferença.",
        ],
        aliases: ["securitização", "securitizacao", "operação estruturada"],
      },
      {
        id: "lastro",
        title: "Lastro",
        summary: "Créditos que dão base econômica à emissão de um título.",
        bullets: [
          "Em CRIs, o lastro deve possuir natureza imobiliária conforme a regulamentação aplicável.",
          "Avalie qualidade, pulverização, prazo e capacidade de pagamento dos créditos.",
          "Lastro forte não substitui a leitura das garantias e dos covenants.",
        ],
        aliases: ["créditos lastro", "recebíveis", "carteira de recebíveis"],
      },
      {
        id: "ltv",
        title: "LTV — Loan to Value",
        summary:
          "Relaciona o saldo da dívida ao valor do ativo ou das garantias que dão suporte à operação de crédito.",
        formula: "LTV = saldo da dívida ÷ valor do colateral × 100",
        bullets: [
          "Um LTV menor pode oferecer mais margem de garantia, dependendo da qualidade, liquidez e avaliação do colateral.",
          "A relação pode mudar com novas dívidas, amortizações, reavaliações e variações no valor das garantias.",
          "Leia o LTV junto de senioridade, subordinação, covenants, concentração e capacidade de pagamento.",
          "LTV é diferente de dívida sobre patrimônio e diferente do Índice de alavancagem calculado pela plataforma.",
        ],
        warning:
          "A plataforma não calcula LTV padronizado por ticker porque não possui, de forma consistente, o saldo da dívida e o valor das garantias de cada operação.",
        aliases: ["loan to value", "dívida sobre garantia", "ltv"],
      },
      {
        id: "spread-credito",
        title: "Spread de crédito",
        summary:
          "Remuneração adicional sobre um indexador ou referência para compensar riscos e características da operação.",
        formula: "Exemplos: IPCA + 7% a.a. · CDI + 2% a.a.",
        bullets: [
          "Taxa maior costuma vir acompanhada de maior risco, prazo ou menor liquidez.",
          "Compare spreads entre operações com estrutura e risco semelhantes.",
          "Não existe retorno extra sem algum fator adicional de risco ou complexidade.",
        ],
        aliases: ["spread", "prêmio de crédito", "taxa adicional"],
      },
      {
        id: "duration-credito",
        title: "Duration e duration modificada",
        summary:
          "Medem o prazo econômico dos fluxos e a sensibilidade aproximada do preço a mudanças nas taxas.",
        formula: "ΔPreço ≈ −Duration modificada × ΔTaxa",
        bullets: [
          "Quanto maior a duration, em geral maior a sensibilidade a mudanças de juros.",
          "A duration não é simplesmente a data de vencimento.",
          "A relação é uma aproximação e depende dos fluxos do título.",
        ],
        aliases: [
          "duration",
          "duration modificada",
          "sensibilidade a juros",
          "prazo econômico",
        ],
      },
      {
        id: "rating",
        title: "Rating",
        summary:
          "Opinião de uma agência de classificação sobre o risco de crédito, segundo sua própria metodologia.",
        bullets: [
          "A escala local e a escala global não são diretamente equivalentes.",
          "O rating pode mudar conforme a situação do emissor e da operação.",
          "Rating não é garantia de pagamento.",
        ],
        aliases: [
          "classificação de risco",
          "grau de investimento",
          "high grade",
          "high yield",
        ],
      },
      {
        id: "garantias-credito",
        title: "Garantias e subordinação",
        summary:
          "Mecanismos que definem quais ativos ou tranches absorvem perdas e em que ordem.",
        bullets: [
          "Alienação fiduciária, cessão fiduciária, hipoteca, fiança e aval têm estruturas diferentes.",
          "Tranches subordinadas podem absorver perdas antes da tranche sênior.",
          "Validade, prioridade, liquidez e execução da garantia são pontos essenciais.",
        ],
        aliases: [
          "garantia real",
          "alienação fiduciária",
          "subordinação",
          "tranche sênior",
        ],
      },
      {
        id: "default",
        title: "Default e recuperação de crédito",
        summary:
          "Default é o descumprimento relevante de uma obrigação; recuperação mede o que pode ser reavido depois.",
        formula: "LGD ≈ 1 − recovery rate",
        bullets: [
          "A recuperação pode ser inferior ao saldo nominal.",
          "Pré-pagamento reduz uma exposição, mas cria risco de reinvestimento.",
          "Observe devedor, garantia, concentração e eventos de covenant.",
        ],
        aliases: [
          "inadimplência",
          "evento de default",
          "recovery rate",
          "lgd",
          "pré-pagamento",
        ],
      },
    ],
  },
  {
    id: "renda-fixa",
    title: "Renda fixa",
    description:
      "Títulos, indexadores e conceitos para comparar retorno, risco, prazo e liquidez.",
    cardDescription: "Títulos, indexadores e conceitos.",
    icon: Percent,
    accent: "amber",
    tags: ["CDI", "IPCA", "Duration", "+15"],
    terms: [
      {
        id: "renda-fixa-conceito",
        title: "Renda fixa",
        summary:
          "Investimento em que a forma de cálculo da remuneração é conhecida no momento da aplicação.",
        bullets: [
          "Isso não significa que o preço não oscile ou que não exista risco.",
          "As formas clássicas são prefixada, pós-fixada e híbrida.",
          "Venda antecipada pode resultar em ganho ou perda por marcação a mercado.",
        ],
        aliases: ["renda fixa", "fixed income", "investimento de renda fixa"],
      },
      {
        id: "cdi",
        title: "CDI — Certificado de Depósito Interbancário",
        summary:
          "CDI significa Certificado de Depósito Interbancário: um título de curtíssimo prazo usado em operações entre instituições financeiras. No dia a dia dos investimentos, a expressão também costuma indicar a Taxa DI, referência para muitas aplicações pós-fixadas.",
        formula:
          "CDB a 110% do CDI ≈ 110% da Taxa DI do período, antes de impostos e custos",
        bullets: [
          "CDI é o instrumento da operação interbancária; Taxa DI é a taxa média apurada nessas operações.",
          "Pessoa física normalmente não compra o CDI diretamente: usa a Taxa DI como referência de remuneração.",
          "O valor exato depende da trajetória do indexador, dos impostos, dos custos e das condições do produto.",
          "Compare o percentual do CDI com o risco, o prazo, a liquidez e o emissor.",
        ],
        aliases: ["taxa di", "di", "percentual do cdi", "% do cdi"],
      },
      {
        id: "ipca",
        title: "IPCA",
        summary:
          "Índice de preços calculado pelo IBGE e principal referência oficial de inflação no Brasil.",
        formula: "IPCA + 6% a.a. = inflação observada + taxa real contratada",
        bullets: [
          "A taxa real busca preservar o poder de compra acima da inflação.",
          "Títulos longos indexados ao IPCA podem oscilar bastante antes do vencimento.",
          "IPCA e IGP-M podem apresentar trajetórias diferentes.",
        ],
        aliases: ["inflação", "índice de preços", "ipca+", "ipca +"],
      },
      {
        id: "prefixado",
        title: "Prefixado, pós-fixado e híbrido",
        summary:
          "As três formas de remuneração combinam previsibilidade, exposição a indexadores e sensibilidade a juros.",
        bullets: [
          "Prefixado: taxa definida na compra.",
          "Pós-fixado: acompanha um indexador como CDI ou Selic.",
          "Híbrido: combina inflação ou outro índice com uma taxa contratada.",
        ],
        aliases: [
          "prefixado",
          "pós-fixado",
          "pos fixado",
          "híbrido",
          "hibrido",
        ],
      },
      {
        id: "marcacao-a-mercado",
        title: "Marcação a mercado",
        summary:
          "Atualiza o preço do título conforme as condições atuais do mercado.",
        formula:
          "Taxa de mercado sobe → preço tende a cair · Taxa cai → preço tende a subir",
        bullets: [
          "A relação é especialmente relevante em títulos prefixados e IPCA+ longos.",
          "Quem leva o título até o vencimento enfrenta uma dinâmica diferente de quem vende antes.",
          "O preço de mercado continua relevante para medir o valor econômico da posição.",
        ],
        aliases: [
          "mtm",
          "marcação na curva",
          "marcacao a mercado",
          "preço do título",
        ],
        cta: { label: "Ver a tela de renda fixa", href: "/landing/renda-fixa" },
      },
      {
        id: "fgc",
        title: "FGC — Fundo Garantidor de Créditos",
        summary:
          "Entidade privada que oferece garantia para determinados produtos elegíveis, dentro de limites.",
        bullets: [
          "A cobertura ordinária é limitada por CPF/CNPJ e por instituição ou conglomerado.",
          "CDB, RDB, LCI e LCA podem ser elegíveis; FIIs, fundos, debêntures, CRI e CRA não têm cobertura ordinária.",
          "FGC reduz um risco específico, mas não torna produtos diferentes igualmente seguros.",
        ],
        warning:
          "Confira as regras e os limites vigentes diretamente nas fontes oficiais antes de tomar uma decisão.",
        aliases: ["fundo garantidor", "garantia fgc", "250 mil", "fgc"],
      },
      {
        id: "cdb-lci-lca",
        title: "CDB, LCI e LCA",
        summary:
          "Títulos bancários com diferentes emissores, prazos, liquidez, tributação e cobertura do FGC.",
        bullets: [
          "CDB é uma captação bancária que pode ser prefixada, pós-fixada ou híbrida.",
          "LCI e LCA possuem lastros específicos e podem ter rendimentos isentos para pessoa física conforme as regras vigentes.",
          "Compare sempre o retorno líquido, a liquidez e o risco do emissor.",
        ],
        aliases: ["cdb", "lci", "lca", "título bancário", "titulos bancarios"],
      },
    ],
  },
];

type ReferenceVisualOptions = Pick<
  ReferenceVisual,
  "marker" | "markerLabel" | "leftLabel" | "rightLabel" | "note"
>;

const makeVisual = (
  kind: ReferenceVisualKind,
  title: string,
  description: string,
  items: ReferenceVisualItem[],
  options: ReferenceVisualOptions = {},
): ReferenceVisual => ({ kind, title, description, items, ...options });

const referenceTermVisuals: Record<string, ReferenceVisual> = {
  "dividend-yield": makeVisual(
    "bars",
    "Renda em relação ao preço",
    "O DY conecta o que foi distribuído com a cotação usada na referência.",
    [
      {
        label: "Rendimentos",
        value: "soma da janela",
        ratio: 68,
        tone: "positive",
      },
      {
        label: "Cotação",
        value: "base de comparação",
        ratio: 100,
        tone: "muted",
      },
    ],
    {
      note: "Exemplo de relação — não representa o DY de um fundo específico.",
    },
  ),
  "p-vp": makeVisual(
    "comparison",
    "Preço versus patrimônio",
    "A pergunta visual é simples: a cota está acima, próxima ou abaixo do VP/cota?",
    [
      { label: "Preço", value: "0,80×", ratio: 80, tone: "accent" },
      { label: "VP/cota", value: "1,00×", ratio: 100, tone: "muted" },
    ],
    {
      note: "Exemplo de desconto relativo. Desconto não é sinônimo de preço justo.",
    },
  ),
  "vp-cota": makeVisual(
    "flow",
    "De patrimônio para uma cota",
    "O valor patrimonial por cota nasce da divisão do patrimônio líquido pelo número de cotas.",
    [
      { label: "Patrimônio líquido", value: "imóveis, caixa e obrigações" },
      { label: "÷ cotas", value: "quantidade emitida" },
      { label: "VP/cota", value: "valor atribuído a cada cota" },
    ],
    {
      note: "A conta é ilustrativa; a composição do patrimônio varia por fundo.",
    },
  ),
  "liquidez-media": makeVisual(
    "bars",
    "Facilidade de executar uma ordem",
    "A média diária resume o volume financeiro negociado nos pregões observados.",
    [
      {
        label: "Pregão mais fraco",
        value: "menos negócios",
        ratio: 28,
        tone: "muted",
      },
      { label: "Média de 30d", value: "referência", ratio: 62, tone: "accent" },
      {
        label: "Pregão mais forte",
        value: "mais negócios",
        ratio: 90,
        tone: "positive",
      },
    ],
    { note: "Volume maior ajuda na execução, mas não garante preço melhor." },
  ),
  "vacancia-fisica": makeVisual(
    "scale",
    "Área ocupada e área vaga",
    "A vacância olha para o espaço físico que não está ocupado, e não diretamente para a receita.",
    [],
    {
      marker: 22,
      markerLabel: "área vaga (exemplo)",
      leftLabel: "mais ocupada",
      rightLabel: "mais vaga",
      note: "Escala ilustrativa — confirme a metodologia e a cobertura do informe.",
    },
  ),
  inadimplencia: makeVisual(
    "scale",
    "Receita no prazo e receita em atraso",
    "O percentual ajuda a localizar a parcela de receitas imobiliárias que não entrou no prazo esperado.",
    [],
    {
      marker: 12,
      markerLabel: "em atraso (exemplo)",
      leftLabel: "menos atraso",
      rightLabel: "mais atraso",
      note: "Escala ilustrativa — um único devedor pode ser relevante mesmo em uma média baixa.",
    },
  ),
  "volatilidade-21d": makeVisual(
    "scale",
    "Oscilação recente da cotação",
    "A volatilidade transforma a variação dos pregões recentes em uma régua anualizada.",
    [],
    {
      marker: 58,
      markerLabel: "oscilação recente (exemplo)",
      leftLabel: "oscila menos",
      rightLabel: "oscila mais",
      note: "A régua mede oscilação passada, não a qualidade do fundo nem uma previsão.",
    },
  ),
  "dy-mes": makeVisual(
    "bars",
    "Uma distribuição dentro da série",
    "O DY do mês é um ponto de uma sequência: ele ganha sentido quando lido com os meses vizinhos.",
    [
      { label: "Mês −2", value: "menor", ratio: 45, tone: "muted" },
      { label: "Mês −1", value: "maior", ratio: 78, tone: "positive" },
      { label: "Mês atual", value: "referência", ratio: 58, tone: "accent" },
    ],
    {
      note: "Exemplo de série mensal — não anualize um mês isolado automaticamente.",
    },
  ),
  "crescimento-dy": makeVisual(
    "comparison",
    "Duas janelas de renda",
    "A comparação coloca a soma dos rendimentos dos últimos 12 meses contra os 12 meses anteriores.",
    [
      { label: "Janela anterior", value: "100", ratio: 58, tone: "muted" },
      { label: "Janela atual", value: "120", ratio: 76, tone: "positive" },
    ],
    { note: "Escala relativa ilustrativa — não é retorno total do cotista." },
  ),
  "desconto-pvp-historico": makeVisual(
    "scale",
    "P/VP atual contra a própria média",
    "Em vez de usar 1,00 como único marco, a régua compara o fundo com sua história recente.",
    [],
    {
      marker: 34,
      markerLabel: "P/VP atual (exemplo)",
      leftLabel: "abaixo da média",
      rightLabel: "acima da média",
      note: "A média histórica é contexto, não uma estimativa de valor justo.",
    },
  ),
  "distancia-maxima-12m": makeVisual(
    "scale",
    "Preço atual e topo recente",
    "A distância mostra a posição da cotação em relação à máxima observada na janela.",
    [],
    {
      marker: 42,
      markerLabel: "cotação atual (exemplo)",
      leftLabel: "perto da máxima",
      rightLabel: "mais distante",
      note: "Estar distante da máxima não define se a cota está barata.",
    },
  ),
  "crescimento-vp-cota": makeVisual(
    "comparison",
    "Patrimônio por cota ao longo do tempo",
    "A leitura compara o VP/cota atual com o ponto de aproximadamente 12 meses antes.",
    [
      { label: "Há 12 meses", value: "100", ratio: 62, tone: "muted" },
      { label: "Atual", value: "108", ratio: 72, tone: "positive" },
    ],
    {
      note: "Variação patrimonial ilustrativa — não inclui automaticamente os dividendos.",
    },
  ),
  "caixa-liquidez-fundo": makeVisual(
    "flow",
    "O que sobra depois das obrigações",
    "A liquidez líquida organiza recursos disponíveis, valores a receber e compromissos reportados.",
    [
      { label: "Disponibilidades", value: "caixa" },
      { label: "+ recebíveis", value: "valores a receber" },
      { label: "− obrigações", value: "compromissos" },
      { label: "Liquidez líquida", value: "folga observada" },
    ],
    {
      note: "A composição depende da competência e dos campos informados na fonte.",
    },
  ),
  "pressao-caixa": makeVisual(
    "timeline",
    "Meses sob pressão",
    "A série destaca competências em que o caixa líquido ficou negativo e a direção da mudança.",
    [
      { label: "−3", value: "estável", tone: "muted" },
      { label: "−2", value: "sob pressão", tone: "warning" },
      { label: "−1", value: "sob pressão", tone: "warning" },
      { label: "Atual", value: "recuperando", tone: "positive" },
    ],
    { note: "Linha do tempo ilustrativa — não é uma previsão de dividendos." },
  ),
  "concentracao-tijolo-papel": makeVisual(
    "stack",
    "Como o perfil patrimonial se divide",
    "A barra mostra que a proporção de tijolo e papel é uma leitura de composição, não de quantidade de cotas.",
    [
      { label: "Tijolo", value: "maior parcela", ratio: 68, tone: "accent" },
      { label: "Papel", value: "menor parcela", ratio: 32, tone: "muted" },
    ],
    {
      note: "Composição ilustrativa — cobertura incompleta pode impedir que a barra some 100%.",
    },
  ),
  "sensibilidade-selic": makeVisual(
    "scale",
    "Exposição a cenários de juros",
    "O score ajuda a organizar características que podem reagir de forma diferente a mudanças na Selic.",
    [],
    {
      marker: 64,
      markerLabel: "score composto (exemplo)",
      leftLabel: "menor sensibilidade",
      rightLabel: "maior sensibilidade",
      note: "É um índice próprio da plataforma, não uma previsão do Banco Central.",
    },
  ),
  "indice-alavancagem": makeVisual(
    "scale",
    "Obrigações e folga financeira",
    "O índice contextualiza passivos, caixa, recebíveis e patrimônio antes de aprofundar a dívida.",
    [],
    {
      marker: 48,
      markerLabel: "pressão financeira (exemplo)",
      leftLabel: "mais folga",
      rightLabel: "mais pressão",
      note: "Não é LTV e não substitui a leitura de vencimentos e garantias.",
    },
  ),
  fii: makeVisual(
    "flow",
    "O caminho do investimento coletivo",
    "O fundo reúne recursos, administra ativos e distribui resultados conforme sua política.",
    [
      { label: "Cotistas", value: "recursos" },
      { label: "FII", value: "gestão e carteira" },
      { label: "Ativos", value: "imóveis ou crédito" },
      { label: "Resultados", value: "distribuições" },
    ],
    {
      note: "Esquema conceitual — a política de cada fundo define os ativos permitidos.",
    },
  ),
  cota: makeVisual(
    "stack",
    "Um patrimônio dividido em frações",
    "A cota representa uma fração ideal do fundo; não é a escritura de um imóvel específico.",
    [
      {
        label: "Patrimônio do fundo",
        value: "100%",
        ratio: 100,
        tone: "muted",
      },
      { label: "Uma cota", value: "fração", ratio: 18, tone: "accent" },
    ],
    { note: "Proporções ilustrativas para explicar a ideia de fração ideal." },
  ),
  "fii-tijolo": makeVisual(
    "stack",
    "Renda ligada a imóveis",
    "A análise começa no imóvel, passa pelo locatário e chega à renda distribuída pelo fundo.",
    [
      { label: "Logística", value: "galpões", ratio: 35, tone: "accent" },
      { label: "Escritórios", value: "lajes", ratio: 25, tone: "muted" },
      { label: "Shoppings", value: "varejo", ratio: 22, tone: "positive" },
      { label: "Outros", value: "segmentos", ratio: 18, tone: "warning" },
    ],
    { note: "Exemplo de composição — não descreve um fundo específico." },
  ),
  "fii-papel": makeVisual(
    "flow",
    "Renda ligada a recebíveis",
    "A renda depende do fluxo de pagamentos, dos indexadores e da proteção da operação de crédito.",
    [
      { label: "Devedor", value: "paga o crédito" },
      { label: "CRI", value: "estrutura o fluxo" },
      { label: "FII", value: "administra a carteira" },
      { label: "Cotista", value: "recebe resultados" },
    ],
    { note: "O risco depende da operação, não apenas do rótulo papel." },
  ),
  fof: makeVisual(
    "flow",
    "Um fundo dentro de outros fundos",
    "O FoF adiciona uma camada de seleção e pode diversificar, mas também acumula custos e decisões.",
    [
      { label: "Cotista", value: "investe no FoF" },
      { label: "FoF", value: "aloca recursos" },
      { label: "FIIs investidos", value: "carteiras" },
    ],
    {
      note: "Fluxo conceitual — observe custos em camadas e a estratégia do gestor.",
    },
  ),
  "emissao-cotas": makeVisual(
    "timeline",
    "Capital novo em três movimentos",
    "Uma emissão altera o número de cotas e pode financiar aquisições, projetos ou redução de dívida.",
    [
      { label: "Oferta", value: "novas cotas", tone: "accent" },
      { label: "Captação", value: "recursos", tone: "positive" },
      { label: "Uso", value: "ativos ou dívida", tone: "muted" },
    ],
    {
      note: "Leia preço, direito de preferência e destinação dos recursos no documento da oferta.",
    },
  ),
  "amortizacao-cotas": makeVisual(
    "flow",
    "Capital voltando ao cotista",
    "A amortização reduz parte do investimento econômico e não deve ser confundida com renda recorrente.",
    [
      { label: "Venda ou principal", value: "origem do caixa" },
      { label: "Fundo", value: "decide amortizar" },
      { label: "Cotista", value: "recebe capital" },
    ],
    { note: "O comunicado explica a origem, o valor e a regra do evento." },
  ),
  "relatorio-gerencial": makeVisual(
    "timeline",
    "Documento para acompanhar mudanças",
    "O relatório conecta números, acontecimentos e a visão da gestão em uma competência.",
    [
      { label: "Números", value: "resultado", tone: "accent" },
      { label: "Eventos", value: "mudanças", tone: "warning" },
      { label: "Comentário", value: "contexto", tone: "positive" },
    ],
    {
      note: "Complemente a visão da gestão com documentos regulatórios e histórico.",
    },
  ),
  abl: makeVisual(
    "comparison",
    "Área disponível para locação",
    "A ABL cria uma base para comparar ocupação, aluguel e vendas em empreendimentos.",
    [
      { label: "Área ocupada", value: "em uso", ratio: 78, tone: "positive" },
      { label: "Área vaga", value: "disponível", ratio: 22, tone: "warning" },
    ],
    {
      note: "Proporções ilustrativas — a definição de ABL pode variar por relatório.",
    },
  ),
  "contrato-tipico": makeVisual(
    "timeline",
    "Contrato com regras gerais de locação",
    "Prazo, reajuste, revisão, garantias e capacidade do locatário formam a leitura completa.",
    [
      { label: "Início", value: "locação", tone: "accent" },
      { label: "Reajustes", value: "inflação", tone: "muted" },
      { label: "Revisional", value: "mercado", tone: "warning" },
      { label: "Vencimento", value: "renovação", tone: "positive" },
    ],
    {
      note: "A classificação típica não informa sozinha a segurança da renda.",
    },
  ),
  "contrato-atipico": makeVisual(
    "timeline",
    "Obrigações econômicas pactuadas",
    "Contratos atípicos podem alongar a previsibilidade, mas concentram condições específicas de execução.",
    [
      { label: "Obra ou entrega", value: "marco inicial", tone: "accent" },
      { label: "Prazo longo", value: "renda contratada", tone: "positive" },
      { label: "Saída", value: "multa e condições", tone: "warning" },
    ],
    {
      note: "Prazo longo não elimina risco de crédito, execução ou concentração.",
    },
  ),
  "built-to-suit": makeVisual(
    "flow",
    "Imóvel desenhado para um uso",
    "A estrutura começa na necessidade do ocupante e termina em um contrato que precisa ser executado.",
    [
      { label: "Necessidade", value: "uso específico" },
      { label: "Desenvolvimento", value: "obra e licenças" },
      { label: "Ocupante", value: "contrato longo" },
    ],
    { note: "Avalie crédito do ocupante e valor de reutilização do imóvel." },
  ),
  "sale-leaseback": makeVisual(
    "flow",
    "Venda do imóvel, permanência como locatário",
    "A empresa libera capital ao vender o imóvel e continua pagando aluguel ao fundo.",
    [
      { label: "Empresa", value: "vende o imóvel" },
      { label: "Fundo", value: "compra o ativo" },
      { label: "Empresa", value: "segue locatária" },
    ],
    {
      note: "A permanência do antigo proprietário não elimina o risco de locatário.",
    },
  ),
  noi: makeVisual(
    "bars",
    "Receita operacional menos despesas",
    "O NOI isola a operação do imóvel antes de itens financeiros e outras despesas não operacionais.",
    [
      {
        label: "Receitas",
        value: "aluguéis e operação",
        ratio: 100,
        tone: "positive",
      },
      {
        label: "Despesas",
        value: "custos operacionais",
        ratio: 34,
        tone: "warning",
      },
      {
        label: "NOI",
        value: "resultado operacional",
        ratio: 66,
        tone: "accent",
      },
    ],
    {
      note: "A metodologia pode variar; compare fontes com a mesma definição.",
    },
  ),
  revisional: makeVisual(
    "comparison",
    "Aluguel atual e aluguel de mercado",
    "A revisional pode aumentar ou reduzir a receita quando o contrato permite reavaliar o aluguel.",
    [
      { label: "Aluguel atual", value: "contrato", ratio: 58, tone: "muted" },
      { label: "Mercado", value: "referência", ratio: 76, tone: "accent" },
    ],
    { note: "Exemplo relativo — não é a aplicação automática de um reajuste." },
  ),
  "concentracao-inquilino": makeVisual(
    "stack",
    "Quem sustenta a receita",
    "A barra ajuda a perceber quando poucos locatários concentram a receita ou a área do fundo.",
    [
      {
        label: "Maior locatário",
        value: "parcela principal",
        ratio: 52,
        tone: "warning",
      },
      {
        label: "Demais locatários",
        value: "restante",
        ratio: 48,
        tone: "muted",
      },
    ],
    {
      note: "Concentração ilustrativa — avalie setor, crédito, prazo e reposição.",
    },
  ),
  "cap-rate": makeVisual(
    "bars",
    "Renda operacional sobre o valor do imóvel",
    "O cap rate aproxima a taxa operacional de um imóvel, sem ser o DY distribuído pelo FII.",
    [
      { label: "NOI anualizado", value: "renda", ratio: 34, tone: "positive" },
      { label: "Valor do imóvel", value: "base", ratio: 100, tone: "muted" },
    ],
    {
      note: "Exemplo de relação — a plataforma não calcula cap rate padronizado por ticker.",
    },
  ),
  wault: makeVisual(
    "timeline",
    "Quando os contratos vencem",
    "O WAULT resume prazos restantes ponderados; a distribuição dos vencimentos continua importante.",
    [
      { label: "Curto prazo", value: "renovações próximas", tone: "warning" },
      { label: "Médio prazo", value: "contratos vigentes", tone: "accent" },
      { label: "Longo prazo", value: "receita contratada", tone: "positive" },
    ],
    {
      note: "A plataforma não calcula WAULT padronizado para todos os fundos.",
    },
  ),
  cri: makeVisual(
    "flow",
    "O caminho de um recebível imobiliário",
    "O CRI transforma créditos imobiliários em um título cuja estrutura define os fluxos e proteções.",
    [
      { label: "Créditos", value: "lastro" },
      { label: "Securitizadora", value: "emissão" },
      { label: "Investidor", value: "fluxo de pagamentos" },
    ],
    { note: "CRI não possui cobertura ordinária do FGC." },
  ),
  securitizacao: makeVisual(
    "flow",
    "Créditos transformados em títulos",
    "A estrutura organiza lastro, garantias, prioridades e fluxos para investidores.",
    [
      { label: "Direitos creditórios", value: "origem" },
      { label: "Estrutura", value: "garantias e regras" },
      { label: "Título", value: "negociação" },
    ],
    {
      note: "A qualidade da operação depende dos documentos e de quem paga o fluxo.",
    },
  ),
  lastro: makeVisual(
    "stack",
    "A base econômica do título",
    "A análise do lastro pergunta de onde vem o pagamento e quão diversificados são os créditos.",
    [
      {
        label: "Devedores",
        value: "capacidade de pagar",
        ratio: 42,
        tone: "accent",
      },
      { label: "Garantias", value: "proteção", ratio: 28, tone: "positive" },
      {
        label: "Fluxos",
        value: "prazo e recebimento",
        ratio: 30,
        tone: "muted",
      },
    ],
    {
      note: "Uma boa garantia não substitui a análise do devedor e do contrato.",
    },
  ),
  ltv: makeVisual(
    "scale",
    "Dívida em relação à garantia",
    "O LTV compara o saldo da dívida com o valor do colateral que dá suporte à operação.",
    [],
    {
      marker: 54,
      markerLabel: "dívida / garantia (exemplo)",
      leftLabel: "mais margem",
      rightLabel: "menos margem",
      note: "Escala ilustrativa — a plataforma não calcula LTV padronizado por ticker.",
    },
  ),
  "spread-credito": makeVisual(
    "bars",
    "Referência mais prêmio de crédito",
    "O spread é a remuneração adicional sobre CDI, IPCA ou outra referência.",
    [
      { label: "Indexador", value: "base", ratio: 62, tone: "muted" },
      { label: "Spread", value: "prêmio", ratio: 24, tone: "accent" },
      { label: "Remuneração", value: "total", ratio: 86, tone: "positive" },
    ],
    {
      note: "Prêmio maior costuma vir com risco, prazo ou complexidade maiores.",
    },
  ),
  "duration-credito": makeVisual(
    "timeline",
    "Quando os fluxos chegam",
    "A duration pondera o tempo econômico dos pagamentos e ajuda a explicar a sensibilidade a juros.",
    [
      { label: "Hoje", value: "entrada", tone: "accent" },
      { label: "Fluxos", value: "cupons e principal", tone: "muted" },
      { label: "Vencimento", value: "principal final", tone: "positive" },
    ],
    { note: "Duration não é simplesmente a data de vencimento." },
  ),
  rating: makeVisual(
    "scale",
    "Uma escala de opinião de crédito",
    "O rating organiza a opinião de uma agência, mas não garante pagamento nem elimina a necessidade de análise.",
    [],
    {
      marker: 72,
      markerLabel: "classificação (exemplo)",
      leftLabel: "maior risco",
      rightLabel: "menor risco",
      note: "As escalas e metodologias variam por agência e por emissão.",
    },
  ),
  "garantias-credito": makeVisual(
    "flow",
    "Quem absorve uma perda primeiro",
    "Garantias e subordinação definem proteção, prioridade e ordem de absorção de perdas.",
    [
      { label: "Ativos", value: "garantia" },
      { label: "Tranche subordinada", value: "absorve primeiro" },
      { label: "Tranche sênior", value: "prioridade maior" },
    ],
    {
      note: "A validade e a possibilidade de execução importam tanto quanto o nome da garantia.",
    },
  ),
  default: makeVisual(
    "flow",
    "Do evento à recuperação",
    "Um default interrompe o fluxo esperado; a recuperação depende de devedor, garantia e execução.",
    [
      { label: "Evento", value: "obrigação não paga", tone: "warning" },
      { label: "Negociação", value: "cobrança e estrutura", tone: "accent" },
      { label: "Recuperação", value: "parte reavida", tone: "positive" },
    ],
    { note: "A recuperação pode ser inferior ao saldo nominal." },
  ),
  "renda-fixa-conceito": makeVisual(
    "flow",
    "Remuneração conhecida, preço variável",
    "A regra de cálculo é conhecida, mas o valor de mercado pode oscilar antes do vencimento.",
    [
      { label: "Aplicação", value: "taxa e prazo" },
      { label: "Mercado", value: "preço oscila" },
      { label: "Vencimento", value: "regra contratada" },
    ],
    {
      note: "Renda fixa não significa ausência de risco ou de variação de preço.",
    },
  ),
  cdi: makeVisual(
    "flow",
    "Do mercado interbancário à sua aplicação",
    "O CDI é o instrumento; a Taxa DI resume os juros dessas operações e vira referência para investimentos.",
    [
      { label: "Bancos", value: "empréstimos de 1 dia", tone: "muted" },
      { label: "CDI", value: "título interbancário", tone: "accent" },
      { label: "Taxa DI", value: "média das operações", tone: "positive" },
      {
        label: "Investimento",
        value: "ex.: 110% da Taxa DI",
        tone: "warning",
      },
    ],
    {
      note: "A remuneração final depende do período, dos impostos, dos custos e das condições do produto.",
    },
  ),
  ipca: makeVisual(
    "stack",
    "Inflação mais taxa real",
    "O retorno híbrido combina a variação de preços com uma remuneração contratada acima dela.",
    [
      {
        label: "Inflação",
        value: "protege poder de compra",
        ratio: 54,
        tone: "warning",
      },
      {
        label: "Taxa real",
        value: "remuneração adicional",
        ratio: 46,
        tone: "accent",
      },
    ],
    {
      note: "Títulos longos IPCA+ podem oscilar bastante antes do vencimento.",
    },
  ),
  prefixado: makeVisual(
    "comparison",
    "Três formas de combinar taxa e indexador",
    "A escolha muda a previsibilidade da remuneração e a exposição à trajetória dos juros.",
    [
      { label: "Prefixado", value: "taxa definida", ratio: 82, tone: "accent" },
      {
        label: "Pós-fixado",
        value: "acompanha índice",
        ratio: 64,
        tone: "positive",
      },
      { label: "Híbrido", value: "índice + taxa", ratio: 73, tone: "muted" },
    ],
    {
      note: "O desenho é conceitual; risco, prazo e liquidez continuam decisivos.",
    },
  ),
  "marcacao-a-mercado": makeVisual(
    "scale",
    "Taxa e preço caminham em sentidos opostos",
    "Quando a taxa exigida pelo mercado muda, o preço de negociação tende a se ajustar na direção contrária.",
    [],
    {
      marker: 50,
      markerLabel: "preço de referência",
      leftLabel: "taxa cai · preço sobe",
      rightLabel: "taxa sobe · preço cai",
      note: "A relação é mais relevante antes do vencimento, especialmente em prazos longos.",
    },
  ),
  fgc: makeVisual(
    "flow",
    "Proteção com produto e limite",
    "A cobertura do FGC depende da elegibilidade, do CPF/CNPJ e da instituição ou conglomerado.",
    [
      { label: "Produto elegível", value: "regra aplicável" },
      { label: "Instituição", value: "limite por conglomerado" },
      { label: "Investidor", value: "limite por CPF/CNPJ" },
    ],
    {
      note: "FIIs, fundos, debêntures, CRI e CRA não têm cobertura ordinária do FGC.",
    },
  ),
  "cdb-lci-lca": makeVisual(
    "comparison",
    "Produtos bancários com escolhas diferentes",
    "Compare emissor, liquidez, tributação, prazo e cobertura — não apenas a taxa anunciada.",
    [
      { label: "CDB", value: "captação bancária", ratio: 72, tone: "accent" },
      {
        label: "LCI",
        value: "lastro imobiliário",
        ratio: 58,
        tone: "positive",
      },
      {
        label: "LCA",
        value: "lastro do agronegócio",
        ratio: 58,
        tone: "muted",
      },
    ],
    {
      note: "O retorno líquido e a liquidez podem mudar a comparação entre produtos.",
    },
  ),
};

const fallbackReferenceVisual = (term: ReferenceTerm): ReferenceVisual =>
  makeVisual(
    "reading",
    `Como visualizar ${term.title}`,
    term.summary,
    [
      { label: "Definição", value: term.title, tone: "accent" },
      {
        label: "Observe",
        value: term.bullets?.[0] ?? "Leia o conceito junto de outros dados.",
        tone: "muted",
      },
      {
        label: "Cuidado",
        value:
          term.warning ??
          term.bullets?.[1] ??
          "Confira a fonte e a data de referência.",
        tone: "warning",
      },
    ],
    { note: "Use esta anotação junto da explicação completa do termo." },
  );

export const referenceSections: ReferenceSection[] =
  referenceSectionDefinitions.map((section) => ({
    ...section,
    terms: section.terms.map((term) => ({
      ...term,
      visual:
        term.visual ??
        referenceTermVisuals[term.id] ??
        fallbackReferenceVisual(term),
    })),
  }));

export const glossaryEntries = [
  [
    "A",
    "ABL — Área Bruta Locável",
    "Área considerada disponível para locação em um empreendimento.",
    "abl",
  ],
  [
    "A",
    "Amortização",
    "Devolução de capital ou pagamento parcial do principal ao longo do tempo.",
    "amortizacao-cotas",
  ],
  [
    "B",
    "Built-to-suit",
    "Imóvel desenvolvido ou adaptado para um ocupante específico.",
    "built-to-suit",
  ],
  [
    "C",
    "Caixa e liquidez líquida",
    "Disponibilidades, recebíveis e obrigações usadas para contextualizar a folga financeira.",
    "caixa-liquidez-fundo",
  ],
  [
    "C",
    "Cap rate",
    "Relação entre NOI anualizado e valor do imóvel; ainda não calculado de forma padronizada pela plataforma.",
    "cap-rate",
  ],
  [
    "C",
    "CDI / Taxa DI",
    "Referência usada em muitos investimentos pós-fixados.",
    "cdi",
  ],
  ["C", "Cota", "Fração ideal do patrimônio de um fundo.", "cota"],
  ["C", "CRI", "Certificado de Recebíveis Imobiliários.", "cri"],
  [
    "C",
    "Concentração em tijolo e papel",
    "Participação da maior posição dentro do perfil patrimonial correspondente.",
    "concentracao-tijolo-papel",
  ],
  [
    "C",
    "Crescimento do DY",
    "Variação dos rendimentos distribuídos em 12 meses contra a janela anterior.",
    "crescimento-dy",
  ],
  [
    "C",
    "Crescimento do VP/cota",
    "Variação do valor patrimonial por cota em 12 meses.",
    "crescimento-vp-cota",
  ],
  ["D", "Default", "Descumprimento relevante de uma obrigação.", "default"],
  [
    "D",
    "Desconto P/VP histórico",
    "Diferença entre o P/VP atual e a média histórica recente.",
    "desconto-pvp-historico",
  ],
  [
    "D",
    "Distância da máxima",
    "Percentual pelo qual a cotação está abaixo da máxima de 12 meses.",
    "distancia-maxima-12m",
  ],
  [
    "D",
    "Duration",
    "Medida de prazo econômico e sensibilidade a mudanças de taxas.",
    "duration-credito",
  ],
  [
    "D",
    "DY do mês",
    "Rendimento distribuído no mês em relação à cotação usada na referência.",
    "dy-mes",
  ],
  [
    "F",
    "FGC",
    "Fundo Garantidor de Créditos para produtos elegíveis dentro dos limites.",
    "fgc",
  ],
  [
    "F",
    "FII de papel",
    "Fundo com forte exposição a crédito imobiliário e recebíveis.",
    "fii-papel",
  ],
  [
    "F",
    "FII de tijolo",
    "Fundo que investe predominantemente em imóveis físicos.",
    "fii-tijolo",
  ],
  [
    "G",
    "Garantia real",
    "Garantia vinculada a bens ou direitos específicos.",
    "garantias-credito",
  ],
  [
    "I",
    "Índice de alavancagem",
    "Score próprio para contextualizar obrigações, liquidez e patrimônio do fundo.",
    "indice-alavancagem",
  ],
  [
    "I",
    "Inadimplência média",
    "Percentual médio de receitas imobiliárias em atraso nos dados disponíveis.",
    "inadimplencia",
  ],
  ["I", "IPCA", "Índice oficial de inflação calculado pelo IBGE.", "ipca"],
  [
    "L",
    "Liquidez diária (30d)",
    "Valor financeiro médio negociado nos últimos 30 pregões.",
    "liquidez-media",
  ],
  ["L", "LTV", "Relação entre dívida e valor do colateral.", "ltv"],
  [
    "M",
    "Marcação a mercado",
    "Atualização do preço conforme as condições atuais de mercado.",
    "marcacao-a-mercado",
  ],
  ["N", "NOI", "Resultado operacional líquido de um imóvel.", "noi"],
  [
    "P",
    "P/VP",
    "Relação entre preço de mercado e valor patrimonial por cota.",
    "p-vp",
  ],
  [
    "P",
    "Pressão de caixa",
    "Quantidade de competências dos últimos 12 meses com caixa líquido negativo.",
    "pressao-caixa",
  ],
  ["R", "Rating", "Opinião de agência sobre risco de crédito.", "rating"],
  [
    "R",
    "Renda fixa",
    "Investimento cuja forma de cálculo da remuneração é conhecida.",
    "renda-fixa-conceito",
  ],
  [
    "S",
    "Sensibilidade à Selic",
    "Score próprio para contextualizar a exposição da composição a cenários de juros.",
    "sensibilidade-selic",
  ],
  [
    "S",
    "Spread de crédito",
    "Remuneração adicional sobre um indexador ou referência.",
    "spread-credito",
  ],
  [
    "V",
    "Volatilidade 21d",
    "Oscilação anualizada estimada a partir dos retornos recentes da cotação.",
    "volatilidade-21d",
  ],
  [
    "V",
    "Vacância física",
    "Percentual da área locável que está desocupada.",
    "vacancia-fisica",
  ],
  [
    "V",
    "VP/cota",
    "Patrimônio líquido atribuído a cada cota na data de referência.",
    "vp-cota",
  ],
  ["W", "WAULT", "Prazo médio ponderado restante dos contratos.", "wault"],
  [
    "Y",
    "Yield",
    "Taxa de retorno implícita conforme preço e fluxos considerados.",
    "dividend-yield",
  ],
] as const;

export const referenceCategories: ReferenceCategory[] = [
  ...referenceSections.map(
    ({ id, title, cardDescription, icon, accent, tags }) => ({
      id,
      title,
      cardDescription,
      icon,
      accent,
      tags,
    }),
  ),
  {
    id: "glossario",
    title: "Glossário A–Z",
    cardDescription: "Busca rápida por termos essenciais.",
    icon: BookOpen,
    accent: "sage",
    tags: ["A → Z", "Busca rápida"],
  },
];

export const allReferenceTerms = referenceSections.flatMap((section) =>
  section.terms.map((term) => ({
    ...term,
    sectionId: section.id,
    sectionTitle: section.title,
  })),
);

export const referenceSectionItems = referenceCategories.map(
  ({ id, title }) => ({
    id,
    title,
  }),
);
